import type { AddTenantUserRequest, TenantUser } from '@gat-ship/types'
import { inject, onBeforeUnmount, ref, shallowRef } from 'vue'
import {
  appLoadingKey,
  tenantUsersCollectionKey,
  type AppLoadingContext,
  type TenantUsersCollectionService
} from '@gat-ship/types/injection-keys'
import { useTenantUserApiCalls } from './useTenantUserApiCalls'
import { TenantUserUtils } from '../model/tenant-user-utils'
import { sortTenantUsers } from '../model/tenant-user-selectors'
import { useTenantUserSignalREvents } from '@gat-ship/signalr'
import { globalUtils } from '@gat-ship/utils'

function defineTenantUsersState() {
  const tenantUsers = shallowRef<TenantUser[]>([])

  function setTenantUsers(data: TenantUser[]) {
    tenantUsers.value = sortTenantUsers(data)
  }

  /** A flag to check if the tenant users have been fetched. */
  const isUsersFetched = ref(false)

  const { getTenantUsersRequest } = useTenantUserApiCalls()

  /**
   * Fetches the tenant users.
   * @param options - Optional options for the fetch.
   * @param options.forceFetch - Whether to force a fetch of the tenant users. @default false
   * @param options.subscribeToEvents - Whether to subscribe to tenant user events. @default true
   */
  async function fetchTenantUsers(options?: {
    forceFetch?: boolean
    subscribeToEvents?: boolean
  }) {
    if (!isUsersFetched.value || options?.forceFetch) {
      const result = await getTenantUsersRequest()
      setTenantUsers(result.users)
      isUsersFetched.value = true
    }
    const subscribeToEvents = options?.subscribeToEvents ?? true
    if (subscribeToEvents) {
      signalR.subscribeToTenantUserEvents()
    }
  }

  const signalR = useTenantUserSignalREvents({
    onTenantUserCreated: async () => {
      fetchTenantUsers({
        forceFetch: true
      })
    },
    onTenantUserNameChanged: async () => {
      fetchTenantUsers({
        forceFetch: true
      })
    },
    onTenantUserPhoneNumberChanged: async () => {
      fetchTenantUsers({
        forceFetch: true
      })
    },
    onTenantUserDeactivated: async () => {
      fetchTenantUsers({
        forceFetch: true
      })
    },
    onTenantUserActivated: async () => {
      fetchTenantUsers({
        forceFetch: true
      })
    },
    onTenantAdminGranted: async () => {
      fetchTenantUsers({
        forceFetch: true
      })
    },
    onTenantAdminRevoked: async () => {
      fetchTenantUsers({
        forceFetch: true
      })
    },
    onTenantUserEmailChanged: async () => {
      fetchTenantUsers({
        forceFetch: true
      })
    },
    onTenantUserProfileImageChanged: async () => {
      fetchTenantUsers({
        forceFetch: true
      })
    },
    onUserDeactivationScheduled: async () => {
      fetchTenantUsers({
        forceFetch: true
      })
    },
    onUserActivated: async () => {
      fetchTenantUsers({
        forceFetch: true
      })
    },
    onUserJobTitleChanged: async () => {
      fetchTenantUsers({
        forceFetch: true
      })
    }
  })

  onBeforeUnmount(() => {
    signalR.unsubscribeFromTenantUserEvents()
  })
  return {
    tenantUsers,
    set: setTenantUsers,
    fetch: fetchTenantUsers,
    signalR
  }
}

function defineTenantUserRequests(
  tenantUserState: ReturnType<typeof defineTenantUsersState>,
  loading: AppLoadingContext
) {
  const { addTenantUserRequest } = useTenantUserApiCalls()

  const { setLoadingId, removeLoadingId } = loading

  async function addTenantUser(
    payload: AddTenantUserRequest,
    previewImageUrl: string | undefined
  ) {
    try {
      setLoadingId('add-tenant-user')
      await addTenantUserRequest(payload, globalUtils.functions.uuid())
      const newUser = TenantUserUtils.createTenantUser({
        ...payload,
        imageUrl: previewImageUrl
      })
      tenantUserState.set([...tenantUserState.tenantUsers.value, newUser])
    } finally {
      removeLoadingId('add-tenant-user')
    }
  }

  return {
    addTenantUser
  }
}

/**
 * Collection datasource for tenant users.
 *
 * The loading handle is the app-wide loading registry, provided once at the
 * root by `LoadingProvider` and consumed here via its injection key
 * (`appLoadingKey`, keyed in `@gat-ship/types`) rather than threaded in as a
 * parameter — keeping the datasource UI-free.
 */
export function useTenantUsersCollection(): TenantUsersCollectionService {
  const loading = inject(appLoadingKey)!

  const tenantUserState = defineTenantUsersState()

  const commands = defineTenantUserRequests(tenantUserState, loading)

  return {
    tenantUsers: tenantUserState.tenantUsers,
    fetchTenantUsers: tenantUserState.fetch,
    commands
  }
}

/** Inject the tenant-users collection context provided by a parent provider. */
export function useTenantUsersCollectionContext() {
  return inject(tenantUsersCollectionKey, undefined)
}
